/**
 * Monster base class
 * by littlefean
 */
import { Vector } from '../../core/math/vector';
import { Circle } from '../../core/math/circle';
import { MyColor } from '../../entities/myColor';
import { CircleObject } from '../../entities/base/circleObject';
import {
    renderStatusBar,
    createStatusBarCache,
    BAR_OFFSET,
    BAR_COLORS,
    type StatusBarCache
} from '../../entities/statusBar';
import { MonsterRegistry } from '../monsterRegistry';
import { MONSTER_IMG_PRE_WIDTH, MONSTER_IMG_PRE_HEIGHT, getMonstersImg } from '../monsterConstants';
import { renderMonster } from '../rendering/monsterRenderer';
import { World } from '../../game/world';
import { scaleSpeed, scalePeriod } from '../../core/speedScale';
import { isEnemy } from '@/game/player/ownership';
import {
    calcBulletDodge,
    selectTarget,
    DEFAULT_DODGE_CONFIG,
    DEFAULT_TARGET_CONFIG,
    type DodgeConfig,
    type TargetConfig
} from '../ai';
import type {
    VectorLike as BaseVectorLike,
    CircleLike as BaseCircleLike,
    BulletLike as BaseBulletLike,
    BuildingLike as BaseBuildingLike,
    TerritoryLike,
    UserLike,
    RootBuildingLike as BaseRootBuildingLike,
    FogOfWarLike,
    CheatModeLike,
} from '@/types/worldLike';

// Declare globals for non-migrated modules
declare const EffectLine: {
    acquire(start: VectorLike, end: VectorLike): EffectLineLike;
} | undefined;

declare const EffectCircle: {
    acquire(pos: VectorLike): EffectCircleLike;
} | undefined;

declare const Functions: {
    levelMonsterHpAddedHard(level: number): number;
    levelCollideAddedHard(level: number): number;
    levelAddPrice(level: number): number;
} | undefined;

// Extended VectorLike with additional methods for monster movement
interface VectorLike extends BaseVectorLike {
    copy(): VectorLike;
    sub(other: BaseVectorLike): VectorLike;
    abs(): number;
    add(other: BaseVectorLike): void;
}

// Extended CircleLike with additional methods
interface CircleLike extends BaseCircleLike {
    impact(other: BaseCircleLike): boolean;
    pointIn?(x: number, y: number): boolean;
}

interface EffectLineLike {
    initLineStyle(color: MyColor, width: number): void;
}

interface EffectCircleLike {
    circle: { r: number; fillColor: { setRGBA(r: number, g: number, b: number, a: number): void } };
    animationFunc: () => void;
    flashRedAnimation: () => void;
}

// Extended BulletLike for monster interaction
interface BulletLike extends BaseBulletLike {
    pos: VectorLike;
}

// Extended BuildingLike for monster targeting
interface BuildingLike extends BaseBuildingLike {
    getBodyCircle(): CircleLike;
}

// Extended RootBuildingLike with Vector pos
interface RootBuildingLike extends BaseRootBuildingLike {
    pos: Vector;
}

// Extended FogLike
interface FogLike extends FogOfWarLike {
    isCircleVisible(x: number, y: number, radius: number): boolean;
}

// WorldLike interface for Monster
interface WorldLike {
    width: number;
    height: number;
    // Precomputed world constants (from World class)
    worldCenterX: number;
    worldCenterY: number;
    minMonsterRadius: number;
    monsterRadiusRange: number;
    monsters: Set<Monster>;
    allBullets: Iterable<BulletLike>;
    getBaseBuilding(): RootBuildingLike;
    user: UserLike;
    territory?: TerritoryLike;
    fog?: FogLike;
    cheatMode?: CheatModeLike;
    getMonstersInRange(x: number, y: number, range: number): Monster[];
    getBulletsInRange(x: number, y: number, range: number): BulletLike[];
    getBuildingsInRange(x: number, y: number, range: number): BuildingLike[];
    getAllBuildingArr(): BuildingLike[];
    addMonster(monster: Monster): void;
    removeMonster(monster: Monster): void;
    addEffect(effect: unknown): void;
    addMoneyToOwner(ownerId: string | null, amount: number): void;
}

interface GainDetails {
    gainRadius: number;
    gainFrequency: number;
    gainR: number;
    gainCollideDamageAddNum: number;
    gainHpAddedNum: number;
    gainSpeedNAddNum: number;
    gainHpAddedRate: number;
    gainMaxHpAddedNum: number;
}

interface BulletChangeDetails {
    r: number;
    f: number;
    bulletDR: number;
    bulletAN: number;
    bulletDD: number;
}

type ChangeSpeedFunc = () => void;
type SummonMonsterFunc = (world: WorldLike) => Monster | null;

export class Monster extends CircleObject {
    // Shared Circle object for rendering, avoid creating temporary objects
    static _renderCircle: Circle | null = null;
    // Static Circle for bombSelf collision detection (reused to avoid allocations)
    private static _bombCircle: Circle = new Circle(0, 0, 0);

    static getRenderCircle(x: number, y: number, r: number): Circle {
        if (!Monster._renderCircle) {
            Monster._renderCircle = new Circle(x, y, r);
        } else {
            Monster._renderCircle.x = x;
            Monster._renderCircle.y = y;
            Monster._renderCircle.r = r;
        }
        return Monster._renderCircle;
    }

    // Cached font strings to avoid repeated string creation
    static FONT_12 = "12px Microsoft YaHei";
    static FONT_9 = "9px Microsoft YaHei";

    // 静态变量：缓存的网格尺寸（避免每帧重复计算）
    static _gridWidth: number = 0;
    static _gridHeight: number = 0;

    /**
     * 初始化网格尺寸（在图片加载完成后调用）
     */
    static initGrid(): void {
        const MONSTERS_IMG = getMonstersImg();
        if (MONSTERS_IMG && MONSTERS_IMG.complete && MONSTERS_IMG.naturalWidth > 0) {
            Monster._gridWidth = Math.floor(MONSTERS_IMG.naturalWidth / MONSTER_IMG_PRE_WIDTH);
            Monster._gridHeight = Math.floor(MONSTERS_IMG.naturalHeight / MONSTER_IMG_PRE_HEIGHT);
        }
    }

    name: string;
    gameType: string;
    speedNumb: number;
    speedFreezeNumb: number;
    minFreezeNum: number;
    changedSpeed: Vector;
    changeSpeedFunc: ChangeSpeedFunc;
    maxSpeedN: number;
    burnMaxSpeedN: number;
    burnRate: number;
    maxBurnRate: number;
    colishDamage: number;
    addPrice: number;
    destination: Vector;

    // Pre-allocated vectors for move calculations
    protected _moveVec: Vector;
    protected _tempVec: Vector;

    // Death explosion properties
    bombSelfAble: boolean;
    bombSelfRange: number;
    bombSelfDamage: number;

    // Pass-through ability
    throwAble: boolean;

    // Gravity field
    haveGArea: boolean;
    gAreaR: number;
    gAreaNum: number;

    // Bullet manipulation field
    haveBulletChangeArea: boolean;
    bulletChangeDetails: BulletChangeDetails;

    // Ally buff field
    haveGain: boolean;
    gainDetails: GainDetails;

    // Laser defense ability
    haveLaserDefence: boolean;
    laserFreeze: number;
    laserdefendPreNum: number;
    maxLaserNum: number;
    laserDefendNum: number;
    laserRecoverFreeze: number;
    laserRecoverNum: number;
    laserRadius: number;
    _laserBarCache: StatusBarCache;

    // Death summon ability
    deadSummonAble: boolean;
    summonAble: boolean;
    summonFreezeTime: number;
    summonCount: number;
    summonDistance: number;
    summonMonsterFunc: SummonMonsterFunc;

    // Teleporting ability
    teleportingAble: boolean;
    teleportingRange: number;
    teleportingCount: number;

    // AI: Bullet dodge
    dodgeAble: boolean;
    dodgeConfig: DodgeConfig;
    protected _dodgeVec: Vector;

    // AI: Dynamic target selection
    targetSelectionAble: boolean;
    targetConfig: TargetConfig;
    protected _targetVec: Vector;

    imgIndex: number;

    // Multiplayer: track who dealt the last damage (for kill reward)
    lastDamageOwnerId: string | null = null;
    protected _removedFromWorld: boolean;

    declare world: WorldLike;

    constructor(pos: Vector, world: WorldLike) {
        super(pos, world);
        this.gameType = "Monster";
        this.name = "默认怪物";

        this.speedNumb = scaleSpeed(1);
        this.speedFreezeNumb = 1;
        this.minFreezeNum = 0.2;

        this.changedSpeed = Vector.zero();
        this.changeSpeedFunc = () => {};
        this.maxSpeedN = scaleSpeed(15);
        this.burnMaxSpeedN = 2;
        this.burnRate = 0;
        this.maxBurnRate = 0.005;

        this.hpInit(100);
        this.colishDamage = 100;
        this.r = 15;
        this.addPrice = 5;
        this.destination = new Vector(this.world.worldCenterX, this.world.worldCenterY);
        this.bodyColor = MyColor.arrTo([25, 25, 25, 0.8]);
        this.hpColor = MyColor.arrTo([200, 20, 20, 0.5]);

        this._moveVec = new Vector(0, 0);
        this._tempVec = new Vector(0, 0);

        this.bombSelfAble = false;
        this.bombSelfRange = 100;
        this.bombSelfDamage = 500;

        this.throwAble = false;

        this.haveGArea = false;
        this.gAreaR = 0;
        this.gAreaNum = 0;

        this.haveBulletChangeArea = false;
        this.bulletChangeDetails = {
            r: 100,
            f: 5,
            bulletDR: -0,
            bulletAN: 0,
            bulletDD: -0,
        };

        this.haveGain = false;
        this.gainDetails = {
            gainRadius: 250,
            gainFrequency: 10,
            gainR: 0,
            gainCollideDamageAddNum: 0,
            gainHpAddedNum: 0,
            gainSpeedNAddNum: 0,
            gainHpAddedRate: 0.0,
            gainMaxHpAddedNum: 0,
        };

        this.haveLaserDefence = false;
        this.laserFreeze = scalePeriod(1);
        this.laserdefendPreNum = 10;
        this.maxLaserNum = 1000;
        this.laserDefendNum = 1000;
        this.laserRecoverFreeze = scalePeriod(10);
        this.laserRecoverNum = 10;
        this.laserRadius = 100;
        this._laserBarCache = createStatusBarCache();

        this.deadSummonAble = false;
        this.summonAble = false;
        this.summonFreezeTime = scalePeriod(100);
        this.summonCount = 4;
        this.summonDistance = 30;
        this.summonMonsterFunc = (world) => MonsterRegistry.create('Normal', world) as Monster | null;

        this.teleportingAble = false;
        this.teleportingRange = 100;
        this.teleportingCount = 3;

        // AI: Bullet dodge
        this.dodgeAble = false;
        this.dodgeConfig = { ...DEFAULT_DODGE_CONFIG };
        this._dodgeVec = new Vector(0, 0);

        // AI: Dynamic target selection
        this.targetSelectionAble = false;
        this.targetConfig = { ...DEFAULT_TARGET_CONFIG };
        this._targetVec = new Vector(0, 0);

        this.imgIndex = 0;
        this._removedFromWorld = false;
    }

    static randInit(world: WorldLike, targetBuilding?: { pos: { x: number; y: number } }): Monster {
        // Use specified target building or default to rootBuilding
        let targetPos = targetBuilding?.pos ?? world.getBaseBuilding().pos;
        let minRadius = Math.min(world.width, world.height) * 0.5;
        let maxRadius = Math.max(world.width, world.height) * 0.6;

        let pos = Vector.randCircleOutside(
            targetPos.x, targetPos.y,
            minRadius, maxRadius,
            world.width, world.height
        );

        let res = new this(pos, world);
        res.bodyStrokeColor = MyColor.arrTo([0, 0, 0, 1]);
        return res;
    }

    dataInit(flowNum: number): void {
        if (typeof Functions !== 'undefined') {
            this.hpInit(this.maxHp + Functions.levelMonsterHpAddedHard(flowNum));
            this.colishDamage += Functions.levelCollideAddedHard(flowNum);
            this.addPrice += Functions.levelAddPrice(flowNum);
        }
    }

    teleporting(): void {
        if (this.teleportingAble) {
            // Save old position for spatial grid update
            const oldX = this.pos.x;
            const oldY = this.pos.y;
            
            this.pos.add(Vector.randCircle().mul(this.teleportingRange));
            
            // Update prevX/prevY to new position to avoid sweep detection treating teleport as a line
            this.prevX = this.pos.x;
            this.prevY = this.pos.y;
            
            // Notify spatial grid of position change
            this._markMovement(oldX, oldY);
            
            this.teleportingCount--;
            if (this.teleportingCount < 0) {
                this.teleportingCount = 0;
            }
        }
    }

    selfSwingMove(): void {
        // Use instance temp vectors to avoid GC pressure
        Vector.subTo(this.destination, this.pos, this._moveVec);
        this._moveVec.normalizeInPlace();
        Vector.rotate90To(this._moveVec, this._tempVec);
        this._tempVec.mulInPlace(Math.sin(this.liveTime / scalePeriod(10)) * 10);
        this.changedSpeed.copyFrom(this._tempVec);
    }

    selfSuddenlyMove(): void {
        // Use instance temp vectors to avoid GC pressure
        Vector.subTo(this.destination, this.pos, this._moveVec);
        this._moveVec.normalizeInPlace();
        Vector.mulTo(this._moveVec, (Math.sin(this.liveTime / scalePeriod(10)) + 1) * 2, this._tempVec);
        this.changedSpeed.copyFrom(this._tempVec);
    }

    selfExcitingMove(): void {
        // Use instance temp vectors to avoid GC pressure
        Vector.subTo(this.destination, this.pos, this._moveVec);
        this._moveVec.normalizeInPlace();
        Vector.mulTo(this._moveVec, (Math.sin(this.liveTime / scalePeriod(4)) + 0.3) * 6, this._tempVec);
        this.changedSpeed.copyFrom(this._tempVec);
    }

    selfDoubleSwingMove(): void {
        // Use instance temp vectors to avoid GC pressure
        Vector.subTo(this.destination, this.pos, this._moveVec);
        this._moveVec.normalizeInPlace();
        Vector.rotate90To(this._moveVec, this._tempVec);
        this._tempVec.mulInPlace(Math.sin(Math.pow(this.liveTime / scalePeriod(10), 0.5)) * 10);
        this.changedSpeed.copyFrom(this._tempVec);
        // Add second component
        const cos = Math.cos(Math.pow(this.liveTime / scalePeriod(10), 2)) * 20;
        this.changedSpeed.x += this._moveVec.x * cos;
        this.changedSpeed.y += this._moveVec.y * cos;
    }

    /**
     * AI: 躲避子弹行为
     * 检测附近飞来的子弹并计算躲避向量
     */
    selfDodgeMove(): void {
        if (!this.dodgeAble) return;
        calcBulletDodge(this, this.dodgeConfig, this._dodgeVec);
        this.changedSpeed.add(this._dodgeVec);
    }

    /**
     * AI: 动态目标选择
     * 根据策略选择最优攻击目标
     */
    updateTarget(): void {
        if (!this.targetSelectionAble) return;
        const newTarget = selectTarget(this, this.targetConfig, this._targetVec);
        if (newTarget) {
            this.destination.x = newTarget.x;
            this.destination.y = newTarget.y;
        }
    }

    move(): void {
        // 每帧重置 changedSpeed，防止无限累加
        this.changedSpeed.x = 0;
        this.changedSpeed.y = 0;

        // 特殊移动模式（swing, suddenly, exciting等）
        this.changeSpeedFunc();

        // AI: 躲避子弹（累加到 changedSpeed）
        this.selfDodgeMove();

        this._moveVec.x = this.destination.x - this.pos.x;
        this._moveVec.y = this.destination.y - this.pos.y;
        const len = Math.sqrt(this._moveVec.x * this._moveVec.x + this._moveVec.y * this._moveVec.y);
        if (len > 0) {
            this._moveVec.x /= len;
            this._moveVec.y /= len;
        }

        if (this.minFreezeNum > this.speedFreezeNumb) {
            this.speedFreezeNumb = this.minFreezeNum;
        }
        if (this.speedFreezeNumb > this.burnMaxSpeedN) {
            this.speedFreezeNumb = this.burnMaxSpeedN;
        }

        // Acceleration: increase speedNumb over time until maxSpeedN
        if (this.accelerationV > 0 && this.speedNumb < this.maxSpeedN) {
            this.speedNumb += this.accelerationV;
            if (this.speedNumb > this.maxSpeedN) {
                this.speedNumb = this.maxSpeedN;
            }
        }

        let distanceSpeedMul = 1;
        if (len > this.world.minMonsterRadius) {
            const t = Math.min((len - this.world.minMonsterRadius) / this.world.monsterRadiusRange, 1);
            distanceSpeedMul = 1 + 9 * Math.log(1 + t * Math.E) / World._LOG_1_PLUS_E;
        }

        // Fog speed boost: monsters in full shadow move 2x faster
        let fogSpeedMul = 1;
        const fog = this.world.fog;
        if (fog && fog.enabled && !fog.isCircleVisible(this.pos.x, this.pos.y, this.r)) {
            fogSpeedMul = 2;
        }

        const speedMul = this.speedNumb * this.speedFreezeNumb * distanceSpeedMul * fogSpeedMul;
        this.speed.x = this._moveVec.x * speedMul;
        this.speed.y = this._moveVec.y * speedMul;
        this.speed.add(this.changedSpeed);
        
        // Save previous position for sweep collision detection
        this.prevX = this.pos.x;
        this.prevY = this.pos.y;
        
        // Direct position update (skip CircleObject.move's acceleration logic)
        this.pos.add(this.speed);
        
        // Notify spatial grid of position change for accurate range queries
        this._markMovement(this.prevX, this.prevY);
    }

    laserDefend(): void {
        const laserRadiusSq = this.laserRadius * this.laserRadius;
        const inRange = (bullet: BulletLike): boolean => {
            // Use manual distance squared calculation to avoid temporary Vector objects
            const dx = bullet.pos.x - this.pos.x;
            const dy = bullet.pos.y - this.pos.y;
            return dx * dx + dy * dy < laserRadiusSq;
        };

        const defend = (bullet: BulletLike): void => {
            if (inRange(bullet)) {
                let startPos = this.pos.copy();
                startPos.add(Vector.randCircle());
                if (typeof EffectLine !== 'undefined' && EffectLine.acquire) {
                    let e = EffectLine.acquire(startPos, bullet.pos.copy());
                    e.initLineStyle(new MyColor(255, 0, 0, 0.1), 1);
                    this.world.addEffect(e);
                }
                if (typeof EffectCircle !== 'undefined' && EffectCircle.acquire) {
                    let ec = EffectCircle.acquire(bullet.pos.copy());
                    ec.circle.fillColor.setRGBA(255, 0, 0, 0.1);
                    this.world.addEffect(ec);
                }
                bullet.remove();
            }
        };

        const laserNumChange = (dN: number): void => {
            this.laserDefendNum += dN;
            if (this.laserDefendNum < 0) this.laserDefendNum = 0;
            if (this.laserDefendNum > this.maxLaserNum) this.laserDefendNum = this.maxLaserNum;
        };

        if (this.haveLaserDefence) {
            if (this.liveTime % this.laserFreeze === 0) {
                let nearbyBullets = this.world.getBulletsInRange(this.pos.x, this.pos.y, this.laserRadius);
                let count = 0;
                for (let bullet of nearbyBullets) {
                    if (bullet.laserDestoryAble === false) continue;
                    if (count >= this.laserdefendPreNum || this.laserDefendNum <= 0) break;
                    if (inRange(bullet)) {
                        defend(bullet);
                        count++;
                        laserNumChange(-1);
                    }
                }
            }
            if (this.liveTime % this.laserRecoverFreeze === 0) {
                laserNumChange(this.laserRecoverNum);
            }
        }
    }

    gainOther(): void {
        if (this.haveGain) {
            if (this.liveTime % this.gainDetails.gainFrequency === 0) {
                let nearbyMonsters = this.world.getMonstersInRange(this.pos.x, this.pos.y, this.gainDetails.gainRadius + 50);
                const showEffect = this.liveTime % 10 === 0;
                const gainRadiusSq = this.gainDetails.gainRadius * this.gainDetails.gainRadius;
                for (let m of nearbyMonsters) {
                    if (m !== this) {
                        if (m.pos.disSq(this.pos) < gainRadiusSq) {
                            m.colishDamage += this.gainDetails.gainCollideDamageAddNum;
                            m.hpChange(m.maxHp * this.gainDetails.gainHpAddedRate);
                            m.hpChange(this.gainDetails.gainHpAddedNum);
                            m.maxHp += this.gainDetails.gainMaxHpAddedNum;
                            if (m.r > 0 && m.r < 100) {
                                m.r += this.gainDetails.gainR;
                            }
                            if (m.speedNumb < 2.5) {
                                m.speedNumb += this.gainDetails.gainSpeedNAddNum;
                            }
                            if (showEffect && typeof EffectLine !== 'undefined' && EffectLine.acquire) {
                                let e = EffectLine.acquire(this.pos, m.pos);
                                e.initLineStyle(new MyColor(0, 200, 0, 0.5), 3);
                                this.world.addEffect(e);
                            }
                        }
                    }
                }
            }
        }
    }

    bulletChange(): void {
        if (this.haveBulletChangeArea) {
            if (this.liveTime % this.bulletChangeDetails.f === 0) {
                // 使用空间查询优化，避免全局遍历所有子弹
                // 添加 50 的安全边距以覆盖子弹半径
                const bulletsInRange = this.world.getBulletsInRange(
                    this.pos.x,
                    this.pos.y,
                    this.bulletChangeDetails.r + 50
                );
                for (let b of bulletsInRange) {
                    const combinedR = this.bulletChangeDetails.r + b.r;
                    if ((b.pos as Vector).disSq(this.pos) < combinedR * combinedR) {
                        b.bodyRadiusChange(this.bulletChangeDetails.bulletDR);
                        let diffVec = (b.pos as Vector).sub(this.pos);
                        let av = diffVec.to1().mul(this.bulletChangeDetails.bulletAN);
                        let q = 1 - diffVec.abs() / this.bulletChangeDetails.r;
                        av = av.mul(q);
                        b.acceleration = av;
                        b.damageChange(this.bulletChangeDetails.bulletDD);
                    }
                }
            }
        }
    }

    gravyPower(): void {
        if (this.haveGArea) {
            let nearbyBuildings = this.world.getBuildingsInRange(this.pos.x, this.pos.y, this.gAreaR + 50);
            const gAreaRSq = this.gAreaR * this.gAreaR;
            for (let b of nearbyBuildings) {
                // Filter friendly buildings (same owner)
                if (!isEnemy(this, b)) {
                    continue;
                }
                if (this.pos.disSq(b.pos as Vector) < gAreaRSq) {
                    // Power plants (Mine in buildings array) cannot be moved, take damage instead
                    if (b.gameType === "Mine") {
                        b.hpChange(-Math.abs(this.gAreaNum) * 2);
                        continue;
                    }
                    // Use instance temp vector to avoid GC pressure
                    Vector.subTo(this.pos, b.pos as Vector, this._tempVec);
                    this._tempVec.normalizeInPlace().mulInPlace(this.gAreaNum);
                    (b.pos as Vector).add(this._tempVec);
                }
            }
        }
    }

    bombSelf(): void {
        if (this.bombSelfAble) {
            // Reuse static Circle to avoid allocation
            const bC = Monster._bombCircle;
            bC.x = this.pos.x;
            bC.y = this.pos.y;
            bC.r = this.bombSelfRange;
            
            let nearbyBuildings = this.world.getBuildingsInRange(this.pos.x, this.pos.y, this.bombSelfRange + 50);
            for (let b of nearbyBuildings) {
                // Filter friendly buildings (same owner)
                if (!isEnemy(this, b)) {
                    continue;
                }
                if (b.getBodyCircle().impact(bC)) {
                    // Use disSq for distance calculation, only sqrt when needed for damage
                    let disSq = this.pos.disSq(b.pos as Vector);
                    let dis = Math.sqrt(disSq);
                    let damage = (1 - (dis / this.bombSelfRange)) * this.bombSelfDamage;
                    b.hpChange(-Math.abs(damage));
                }
            }
            if (typeof EffectCircle !== 'undefined' && EffectCircle.acquire) {
                let e = EffectCircle.acquire(this.pos.copy());
                e.animationFunc = e.flashRedAnimation;
                e.circle.r = this.bombSelfRange;
                this.world.addEffect(e);
            }
        }
    }

    deadSummon(): void {
        if (this.deadSummonAble) {
            this.summonFunc();
        }
    }

    summonFunc(): void {
        for (let i = 0; i < this.summonCount; i++) {
            let m = this.summonMonsterFunc(this.world);
            if (m) {
                m.pos = this.pos.plus(Vector.randCircle().mul(this.summonDistance));
                this.world.addMonster(m);
            }
        }
    }

    summon(): void {
        if (this.summonAble) {
            if (this.liveTime % this.summonFreezeTime === 0) {
                this.summonFunc();
            }
        }
    }

    hpChange(dh: number, sourceOwnerId?: string | null): void {
        // Track who dealt damage (for kill reward in multiplayer)
        if (dh < 0 && sourceOwnerId !== undefined) {
            this.lastDamageOwnerId = sourceOwnerId;
        }
        return super.hpChange(dh);
    }

    protected shouldSkipPostDeathPhases(): boolean {
        return this._removedFromWorld || this.isDead();
    }

    protected removeIfDead(): boolean {
        if (!this.isDead()) {
            return false;
        }
        if (!this._removedFromWorld) {
            this.bombSelf();
            this.remove();
        }
        return true;
    }

    remove(): void {
        if (this._removedFromWorld) {
            return;
        }
        this._removedFromWorld = true;
        this.deadSummon();
        super.remove();
        this.hpSet(0);
        this.world.removeMonster(this);
        // Kill reward: dispatch to killer (multiplayer compatible)
        this.world.addMoneyToOwner(this.lastDamageOwnerId, this.addPrice);
    }

    clash(): void {
        let nearbyBuildings = this.world.getBuildingsInRange(this.pos.x, this.pos.y, this.r + 50);
        const myCircle = this.getBodyCircle();
        for (let b of nearbyBuildings) {
            // Filter friendly buildings (same owner)
            if (!isEnemy(this, b)) {
                continue;
            }
            const bc = b.getBodyCircle();
            if (Circle.collides(myCircle.x, myCircle.y, myCircle.r, bc.x, bc.y, bc.r)) {
                this.bombSelf();
                b.hpChange(-this.colishDamage);
                if (!this.throwAble) {
                    this.remove();
                    break;
                }
            }
        }
    }

    goStep(): void {
        // 保持向后兼容：执行完整的更新逻辑
        this.updateState();
        this.moveOnly();
        this.clashOnly();
    }

    /**
     * 状态更新阶段：处理非移动/碰撞的逻辑
     * 包括：liveTime增加、激光防御、召唤、获取、子弹变化、死亡检查、重力
     */
    updateState(): void {
        super.goStep();
        // AI: 动态目标选择
        this.updateTarget();
        this.laserDefend();
        this.summon();
        this.gainOther();
        this.bulletChange();
        if (this.removeIfDead()) {
            return;
        }
        this.gravyPower();
    }

    /**
     * 移动阶段：只执行移动逻辑
     * 用于分离移动和碰撞检测的两阶段更新
     */
    moveOnly(): void {
        if (this.shouldSkipPostDeathPhases()) {
            return;
        }
        if (this.burnRate > this.maxBurnRate) {
            this.burnRate = this.maxBurnRate;
        }
        if (this.burnRate !== 0) {
            this.hpChange(-this.burnRate * this.maxHp);
        }
        if (this.removeIfDead()) {
            return;
        }
        this.move();
    }

    /**
     * 碰撞检测阶段：只执行碰撞检测逻辑
     * 用于分离移动和碰撞检测的两阶段更新
     * 使用扫掠检测以处理高速移动的穿透问题
     */
    clashOnly(): void {
        if (this.shouldSkipPostDeathPhases()) {
            return;
        }
        let nearbyBuildings = this.world.getBuildingsInRange(this.pos.x, this.pos.y, this.r + 100);
        for (let b of nearbyBuildings) {
            // Skip friendly buildings (multiplayer support)
            if (!isEnemy(this, b)) {
                continue;
            }
            const bc = b.getBodyCircle();
            // 使用扫掠检测（建筑不移动，所以用基础扫掠检测）
            if (Circle.sweepCollides(
                this.prevX, this.prevY, this.pos.x, this.pos.y, this.r,
                bc.x, bc.y, bc.r
            )) {
                this.bombSelf();
                b.hpChange(-this.colishDamage);
                if (!this.throwAble) {
                    this.remove();
                    break;
                }
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        renderMonster(this, ctx);
    }

    getImgStartPosByIndex(n: number): Vector {
        const MONSTERS_IMG = getMonstersImg();
        if (!MONSTERS_IMG || !MONSTERS_IMG.complete || MONSTERS_IMG.naturalWidth === 0) {
            return new Vector(0, 0);
        }
        // 如果网格尺寸未初始化，则初始化（向后兼容）
        if (Monster._gridWidth === 0 || Monster._gridHeight === 0) {
            Monster.initGrid();
        }
        let x = n % Monster._gridWidth;
        let y = Math.floor(n / Monster._gridWidth);
        return new Vector(x * MONSTER_IMG_PRE_WIDTH, y * MONSTER_IMG_PRE_HEIGHT);
    }
}

// Register class type for save system
MonsterRegistry.registerClassType('Monster', () => Monster);
